import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Group from 'components/utils/Group'
import { fillArray } from 'lib/array/arrayUtils'

interface Filters {
  [key: string]: any;
}
interface PaginationFetchParams {
  limit: number;
  offset: number;
  filters?: Filters;
  sort?: string;
}

interface Props {
  children: ReactNode;
  label: string;
  total: number;
  localPage: number;
  itemsPerPage: number;
  fetch: (paginationProps: PaginationFetchParams) => void;
  fetching: boolean;
  filters?: Filters;
  sort?: string;
}

function Pagination(props: Props) {
  const { children, label, total, localPage, itemsPerPage, fetch, fetching, filters, sort } = props

  const [page, setPage] = useState(0)

  const pageNumbers = useMemo(() => {
    const pages = Math.ceil(total / itemsPerPage)
    return fillArray(pages)
  }, [total, itemsPerPage])

  const lastPageNumber = pageNumbers[pageNumbers.length - 1]
  const isLastPage = (page + 1) * itemsPerPage >= total

  const pageButtons = useMemo(() => {
    if (isLastPage) return [lastPageNumber - 1, lastPageNumber]
    else if (page === 0) return [0, 1]
    return [(page - 1), page, (page + 1)]
  }, [isLastPage, lastPageNumber, page])

  // Check if page was changed out Pagination component
  useEffect(() => {
    if (localPage !== page) {
      setPage(localPage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localPage])

  const onChangePage = useCallback((newPage: number) => {
    if (newPage === page) return

    fetch({ limit: itemsPerPage, offset: newPage * itemsPerPage, filters, sort })
    setPage(newPage)
  }, [page, fetch, filters, itemsPerPage, sort])

  return (
    <Group direction="vertical">
      <LoadingIndicator floating visible={fetching} />
      {children}
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <Caption variant="medium">
          {label}: {page * itemsPerPage + 1}-{isLastPage ? total : (page + 1) * itemsPerPage} of {total}
        </Caption>
        {total > itemsPerPage && (
          <Group direction="horizontal" verticalAlign="center" gap={8}>
            {pageNumbers.length > 3 && page >= 2 && (
              <>
                <TextButton kind="tertiary" variant="dark" size="small" onClick={() => onChangePage(0)}>
                  <BodyText variant="small" colour="neutral-four">1</BodyText>
                </TextButton>
                {page >= 3 && <BodyText variant="small">...</BodyText>}
              </>
            )}
            {pageButtons.map(option => (
              <TextButton key={option} kind="tertiary" variant="dark" size="small" onClick={() => onChangePage(option)}>
                <BodyText variant="small" colour={option === page ? 'primary' : 'neutral-four'}>{option + 1}</BodyText>
              </TextButton>
            ))}
            {pageNumbers.length > 3 && page <= (lastPageNumber - 2) && (
              <>
                {page <= (lastPageNumber - 3) && <BodyText variant="small">...</BodyText>}
                <TextButton kind="tertiary" variant="dark" size="small" onClick={() => onChangePage(lastPageNumber)}>
                  <BodyText variant="small" colour={page === lastPageNumber ? 'primary' : 'neutral-four'}>{lastPageNumber + 1}</BodyText>
                </TextButton>
              </>
            )}
          </Group>
        )}
      </Group>
    </Group>
  )
}

export default Pagination
